// errorReporter.js — Built-in, self-hosted client error tracking.
// Captures uncaught JS errors and unhandled promise rejections and reports
// them to POST /api/client-errors (control DB, superadmin health dashboard).
//
// Guard rails:
//  - max 5 reports per page load, deduped by message
//  - reporting failures are swallowed (never cascade)
//  - uses fetch (not the axios instance) to avoid its 401-redirect interceptor,
//    but sends the same X-Requested-With header the CSRF guard requires.

const MAX_REPORTS = 5;
const seen = new Set();
let sent = 0;

function apiBase() {
  // Mirror api.js base resolution without importing it (avoid circular init).
  const baseDomain = process.env.REACT_APP_BASE_DOMAIN || 'welltrack.com.au';
  if (window.location.hostname.endsWith(`.${baseDomain}`)) return '/api';
  const nativeSlug = window.__WT_SCHOOL_SLUG__ || '';
  if (nativeSlug) return `https://${nativeSlug}.${baseDomain}/api`;
  return `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
}

export function reportError(message, stack, source) {
  try {
    const msg = String(message || 'Unknown error').slice(0, 2000);
    if (sent >= MAX_REPORTS || seen.has(msg)) return;
    seen.add(msg);
    sent += 1;
    fetch(`${apiBase()}/client-errors`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'WellTrack',
      },
      body: JSON.stringify({
        message: msg,
        stack: String(stack || '').slice(0, 8000),
        url: window.location.href.slice(0, 500),
        source,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never throw from the error reporter */
  }
}

export function installErrorReporter() {
  window.addEventListener('error', (event) => {
    // Ignore resource-load errors (img/script tags) — only real JS errors.
    if (!event.error && !event.message) return;
    reportError(
      event.message || (event.error && event.error.message),
      event.error && event.error.stack,
      'window_error',
    );
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason || {};
    // Skip axios/network errors — API failures are visible server-side already.
    if (reason.isAxiosError || reason.name === 'AxiosError') return;
    reportError(reason.message || String(reason), reason.stack, 'unhandled_rejection');
  });
}
